<template>
  <div>
    <draggable
      class="list-group"
      tag="ul"
      v-bind="dragOptions"
      @start="drag = true"
      @end="drag = false"
      v-model="additionalFields"
      @change="sortField()"
      v-if="type === 'openEnded' ? additionalFields.length : true"
    >
      <li
        class="list-group--li"
        :class="{'audience-admission--li': type === 'audienceAdmission'}"
        v-for="(item, index) in additionalFields"
        :key="item.id"
      >
        <div class="mb-2 input-group input-option">
          <div class="input-group-prepend handle-drag">
            <div class="input-group-text horizontal layout center center-justified">
              <i class="fas fa-arrows-alt-v" />
            </div>
          </div>
          <select
            class="form-control add-fields--type"
            v-if="item.fieldType !== 'customField'"
            v-model="item.name"
            @change="checkChangeField(item, index)"
          >
            <option
              :value="option"
              v-for="(option, optionIndex) in allowSelectFields"
              :key="optionIndex"
              :disabled="checkShowSelectOption(item.type, option)"
            >
              <span v-if="option === 'team'">{{ $i18nPresenter.t('Organisation') }}</span>
              <span v-else>{{ $i18nPresenter.t(option) | capitalizeFirstLetter }}</span>
            </option>
          </select>
          <InputText
            v-else
            class="name-field"
            :val="item.name"
            :index="index"
            :id="`name-${item.id}`"
            :idName="'additional-field' + item.id"
            @interface="updateKeyField"
            :type="'input-field'"
            :limit="100"
          />
          <InputText
            :val="item.label"
            :index="index"
            :id="index"
            :idName="'additional-field' + item.id"
            @interface="updateField"
            :type="'input-field'"
            :limit="100"
          />
          <div
            class="input-group-prepend pointer"
            @click="removeField(item, index)"
            :class="{'disabled': isRemoveFieldDisabled}"
          >
            <div class="input-group-text horizontal layout center center-justified">
              <i class="far fa-trash-alt" />
            </div>
          </div>
          <div class="mr-0 form-check form-check-inline">
            <div class="custom-control custom-checkbox">
              <div class="custom-control-tooltip">
                <p class="m-0">
                  {{ $i18nPresenter.t('Make this field mandatory') }}
                </p>
              </div>
              <input
                type="checkbox"
                class="custom-control-input"
                :id="'input-field' + index"
                v-model="item.required"
                @change="checkRequiredField(item)"
              >
              <label class="custom-control-label pointer" :for="'input-field' + index" />
            </div>
          </div>
        </div>
      </li>
    </draggable>
    <aha-antd-button
      v-if="additionalFields.length < limitFields"
      v-aha-emit-action
      name="addField"
      type="button"
      iconName="add"
      variant="secondary"
      :iconWidth="16"
      :iconHeight="16"
      class="add-btn"
      @click="addField"
    >
      <span>{{ $i18nPresenter.t('Add') }}</span>
    </aha-antd-button>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import draggable from 'vuedraggable'
import InputText from '@/components/presentations/InputText'
import utils from '@/util'
import EventBus from '@/eventBus'
import eventCategories from '@/constant/eventCategories'

export default {
  components: {
    InputText,
    draggable,
  },
  props: {
    limitFields: {
      type: Number,
      default: 0,
    },
    type: {
      type: String,
      default: '',
    },
    presetFields: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      additionalFields: [],
      allFields: [],
      defaultFields: {},
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
    }),
    allowSelectFields() {
      const allowFields = []
      Object.keys(this.presetFields).forEach((key) => {
        if (this.presetFields[key].fieldType === 'customField') return
        allowFields.push(key)
      })

      return allowFields
    },
    hasAdditionalFields() {
      return this.presentation.audienceAdmission
        && this.presentation.audienceAdmission.fields
        && this.presentation.audienceAdmission.fields.length
    },
    isRemoveFieldDisabled() {
      return this.type === 'audienceAdmission' && this.additionalFields.length === 1
    },
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        delay: 0,
        ghostClass: 'ghost',
        handle: '.handle-drag',
      }
    },
  },
  mounted() {
    this._setFields()
  },
  methods: {
    _setFields() {
      if (this.type === 'openEnded') {
        this.additionalFields = this.slideActive.additionalFields
        this.defaultFields = this.presetFields
        Object.keys(this.presetFields).forEach((item) => {
          this.allFields.push(item)
        })
      } else if (this.type === 'audienceAdmission') {
        if (!this.hasAdditionalFields) {
          this._setDefaultFields()
          this._handleUpdateFields()
        }
        this.additionalFields = this.presentation.audienceAdmission.fields
        this.defaultFields = this.presetFields
        Object.keys(this.presetFields).forEach((item) => {
          this.allFields.push(item)
        })
      }
    },
    _setDefaultFields() {
      const arr = []
      Object.values(this.presetFields).forEach((item) => {
        this._setLabelToLanguage(item)
        arr.push(item)
      })
      this.presentation.audienceAdmission.fields = arr
      this.additionalFields = arr
    },
    checkShowSelectOption(item, option) {
      if (this.additionalFields && this.additionalFields.length > 1) {
        const index = this.additionalFields.findIndex(
          (data) => {
            return data.type === option
          },
        )
        if (item !== option && index !== -1) {
          return true
        }
      }
      return false
    },
    sortField() {
      this._handleUpdateFields()
    },

    _setStoreValue() {
      if (this.type === 'openEnded') {
        this.slideActive.additionalFields = this.additionalFields
      } else if (this.type === 'audienceAdmission') {
        this.presentation.audienceAdmission.fields = this.additionalFields
      }
    },

    checkChangeField(item, index) {
      this.additionalFields[index] = {
        ...this.defaultFields[item.name],
      }
      const option = this._handleSetLabelToLanguage(item.name)
      this.additionalFields[index].label = option.label

      utils.mixpanelTracking(
        'Additional fields',
        'Presenter change field type',
        `Presenter change field, type: ${item.name}`,
      )
      this._handleUpdateFields()
    },

    addField() {
      if (this.additionalFields.length > this.limitFields) return
      let index = 'name'
      let toPush = []
      if (this.additionalFields.length) {
        const allTypes = this.createArrFieldTypes()
        toPush = this.allFields.filter((item) => {
          return allTypes.indexOf(item) === -1
        })
        index = toPush[0]
      }
      const option = this._handleSetLabelToLanguage(index)
      this.additionalFields.push(option)
      utils.mixpanelTracking(
        'Additional fields',
        'Presenter add more field',
        `Presenter add more field, type: ${index}`,
      )

      this._handleUpdateFields()
      EventBus.$emit('on-reDraw-masonry')
    },

    _handleSetLabelToLanguage(index) {
      const option = { ...this.defaultFields[index] }

      this._setLabelToLanguage(option)

      return option
    },

    _setLabelToLanguage(option) {
      if (option.type === 'name') {
        option.label = this.$i18nPresenter.t('enterYourName')
      }
      if (option.type === 'team') {
        option.label = this.$i18nPresenter.t('enterYourOrganisation')
      }
      if (option.type === 'email') {
        option.label = this.$i18nPresenter.t('enterYourEmail')
      }
      if (option.type === 'avatar') {
        option.label = this.$i18nPresenter.t('chooseEmoji')
      }
    },

    removeField(item, index) {
      if (this.isRemoveFieldDisabled) return
      this.additionalFields.splice(index, 1)
      utils.mixpanelTracking(
        'Additional fields',
        'Presenter remove field',
        `Presenter remove field, type: ${item.type}`,
      )
      this._handleUpdateFields()
      EventBus.$emit('on-reDraw-masonry')
    },

    _handleUpdateFields() {
      this._setStoreValue()
      this.$emit('updateAdditionalFields')
    },

    updateKeyField(data) {
      this.additionalFields[data.index].name = data.value
      this._handleUpdateFields()
    },

    updateField(data) {
      this.additionalFields[data.index].label =
        data.value
      this._handleUpdateFields()
    },

    checkRequiredField(item) {
      if (this.type === 'openEnded') {
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Side right > Open ended Additional fields > Check require field',
          `Require field check: ${item.required}, type: ${item.type}`,
        )
      } else if (this.type === 'audienceAdmission') {
        let fieldType = item.type
        if (item.type === 'team') fieldType = 'organisation'
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Settings > Audience admission Additional fields > Check require field',
          `Require field check: ${item.required}, type: ${fieldType}`,
        )
      }
      this._handleUpdateFields()
    },

    createArrFieldTypes() {
      const allTypes = []
      this.additionalFields.forEach((field) => {
        allTypes.push(field.type)
      })
      return allTypes
    },
  },
}
</script>

<style lang="scss" scoped>
// Settings menu uses tailwindcss

.list-group .list-group--li .input-option .name-field.input-style {
  height: 44px;
  flex: none;
  width: 23%;
  min-width: 0;
  padding-left: 0;

  &::v-deep > input {
    padding-right: 10px;
  }

  &::v-deep .value-validate-length {
    display: none;
  }
}
.audience-admission--li {
  @apply list-none;
  .input-group {
    @apply flex;
    & ::v-deep .input-style {
      border: 1px solid #ddd;
      max-width: 100%;
      min-width: 30%;
      border: 1px solid #ddd;
      @apply flex items-center hover:border-primary-90;
      .form-control {
        @apply border-none outline-none overflow-hidden;
        max-width: 100%;
        padding: 0.375rem 0.75rem;
        padding-right: 40px;
        .value-validate-length {
          @apply mb-2;
        }
      }
    }
  }
  .input-group-prepend {
    padding: 0.375rem 0.75rem;
    @apply mb-0 text-center whitespace-nowrap cursor-pointer;
    @apply flex items-center outline-none;
    background-color: #f7fafc;
    border: 1px solid #e9ecef;
    border-radius: 2px;
    width: 44px;
    &.disabled {
      @apply cursor-not-allowed;
    }
  }
  .add-fields--type {
    @apply shadow-none bg-base-0 outline-none hover:border-primary-90;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    border: 1px solid #ddd;
    margin-right: 1px;
  }
  & ::v-deep .input-style {
    @apply flex-auto;
    .form-control {
      padding-right: 40px;
      border: 1px solid #ddd;
    }
  }
  .input-group-text {
    width: 44px;
    margin-left: -1px;
    @apply whitespace-nowrap text-center;
  }
  .custom-control-label {
    &::before, &::after {
      @apply pointer-events-none bg-base-0 shadow-none absolute inline-block;
    }
  }
  .form-check .custom-control {
    @apply relative;
    .custom-control-input {
      @apply absolute opacity-0 cursor-pointer;
      z-index: 1;
      left: 0.5rem;
      top: 0.4rem;
      width: 30px;
      height: 30px;
      &:checked ~ {
        .custom-control-label::after {
          width: 15px;
          height: 15px;
          border: none;
          background-color: transparent;
          overflow: hidden;
          position: absolute;
          top: 14px;
          left: 16px;
        }
      }
    }
  }
}

.form-check {
  min-width: 40px;
  .custom-control {
    padding-left: 0;
    .custom-control-tooltip {
      visibility: hidden;
      width: 154px;
      background-color: rgba(0, 0, 0, 0.9);
      color: rgba(255, 255, 255, 0.9);
      padding: 5px 7px;
      text-align: center;
      border-radius: 2px;
      position: absolute;
      z-index: 1;
      bottom: 159%;
      left: -1.5rem;
      margin-left: -87px;
      opacity: 0;
      transition: opacity 0.2s;
      font-size: 12px;
      &:after {
        content: "";
        position: absolute;
        top: 100%;
        left: 87%;
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: black transparent transparent transparent;
      }
    }
    .custom-control-label {
      position: relative;
      margin-bottom: 0;
      vertical-align: top;
      display: inline-block;
      &::before, &::after {
        width: 32px;
        height: 32px;
        left: 0.5rem;
        border-radius: 8px;
        top: 0.35rem;
        content: "";
        border: 1px solid #ddd;
      }
    }
  .custom-control-input {
    left: 0;
    &:checked ~ {
      .custom-control-label {
        &:after {
          background-color: #ffffff;
          border: 1px solid #adb5bd;
          background-image: url("data:image/svg+xml,%3Csvg aria-hidden='true' focusable='false' data-prefix='fas' data-icon='asterisk' role='img' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' class='svg-inline--fa fa-asterisk fa-w-16 fa-2x'%3E%3Cpath fill='%23e25950' d='M478.21 334.093L336 256l142.21-78.093c11.795-6.477 15.961-21.384 9.232-33.037l-19.48-33.741c-6.728-11.653-21.72-15.499-33.227-8.523L296 186.718l3.475-162.204C299.763 11.061 288.937 0 275.48 0h-38.96c-13.456 0-24.283 11.061-23.994 24.514L216 186.718 77.265 102.607c-11.506-6.976-26.499-3.13-33.227 8.523l-19.48 33.741c-6.728 11.653-2.562 26.56 9.233 33.037L176 256 33.79 334.093c-11.795 6.477-15.961 21.384-9.232 33.037l19.48 33.741c6.728 11.653 21.721 15.499 33.227 8.523L216 325.282l-3.475 162.204C212.237 500.939 223.064 512 236.52 512h38.961c13.456 0 24.283-11.061 23.995-24.514L296 325.282l138.735 84.111c11.506 6.976 26.499 3.13 33.227-8.523l19.48-33.741c6.728-11.653 2.563-26.559-9.232-33.036z' class=''%3E%3C/path%3E%3C/svg%3E");
        }
      }
    }
    &:not(:disabled):active ~,
    &:focus {
      .custom-control-label {
        &:before {
          background-color: #ffffff;
          border: 1px solid #adb5bd;
          transition: none;
          cursor: pointer;
        }
      }
    }
  }
    &:hover {
      .custom-control-tooltip {
        visibility: visible;
        opacity: 1;
      }
    }
    @media (max-width: 812px) {
      .custom-control-tooltip {
        margin-left: -90px;
        &:after {
          left: 89%;
        }
      }
    }
  }
}
// Side right uses bootstrap
.list-group {
  .list-group--li {
    list-style: none;
    .input-option {
      & ::v-deep .input-style {
        width: min-content;
      }
    }
    .add-fields--type {
      height: 44px;
      position: unset;
      flex: none;
      width: 23%;
      padding: 5px;
      text-transform: capitalize;
      margin-right: -1px;
      @media (max-width: 320px) and (orientation: portrait) {
        width: 20%;
      }
    }
    .add-fields--required-box {
      height: 44px;
      width: 40px;
      .add-fields--required {
        border: 1px solid #d0d0d0;
        border-radius: 8px;
        width: 32px;
        height: 32px;
        .custom-control-input {
          left: 50%;
        }
      }
    }
  }
}

.add-btn {
  @apply flex w-full mb-4;
}
</style>
